
import { defineComponent, onMounted, onBeforeUnmount, ref, computed, PropType, nextTick } from 'vue';
import BaseInputText from './BaseInputText.vue';
import BaseTextarea from './BaseTextarea.vue';
import BaseImage from './BaseImage.vue';
import BaseLink from './BaseLink.vue';

export default defineComponent({
  name: 'LazyFieldWrapper',

  components: {
    BaseInputText,
    BaseTextarea,
    BaseImage,
    BaseLink,
  },

  props: {
    // Nom du composant à charger
    componentName: {
      type: String,
      required: true,
    },

    // Props à passer au composant
    componentProps: {
      type: Object,
      required: false,
      default: () => ({}),
    },

    // Events à écouter du composant
    componentEvents: {
      type: Object,
      required: false,
      default: () => ({}),
    },

    // Mode de chargement
    loadMode: {
      type: String,
      required: false,
      default: 'auto',
      validator: (value: string) => ['auto', 'click', 'immediate'].includes(value),
    },

    // Seuil de visibilité
    intersectionThreshold: {
      type: Number,
      required: false,
      default: 0.1,
    },

    // Personnalisation du placeholder
    placeholderText: {
      type: String,
      required: false,
      default: 'Cliquer pour charger',
    },

    placeholderIcon: {
      type: String,
      required: false,
      default: 'fa fa-edit',
    },

    placeholderClass: {
      type: String,
      required: false,
      default: '',
    },

    // Contenu d'aperçu
    previewContent: {
      type: String,
      required: false,
      default: '',
    },

    // Option pour forcer le chargement immédiat
    forceLoad: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const containerRef = ref<HTMLElement>();
    const isLoaded = ref(false);
    let observer: IntersectionObserver | null = null;

    const loadComponent = () => {
      if (!isLoaded.value) {
        isLoaded.value = true;
        if (observer) {
          observer.disconnect();
          observer = null;
        }
      }
    };

    const setupIntersectionObserver = () => {
      if (!containerRef.value || typeof IntersectionObserver === 'undefined') {
        return;
      }

      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio >= props.intersectionThreshold) {
              loadComponent();
            }
          });
        },
        {
          threshold: props.intersectionThreshold,
          rootMargin: '50px',
        }
      );

      observer.observe(containerRef.value);
    };

    onMounted(() => {
      if (props.forceLoad || props.loadMode === 'immediate') {
        loadComponent();
      } else if (props.loadMode === 'auto') {
        nextTick(() => {
          setupIntersectionObserver();
        });
      }
    });

    onBeforeUnmount(() => {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
    });

    return {
      containerRef,
      isLoaded,
      loadComponent,
    };
  },
});
